import React, { useState, useEffect } from 'react';
import { getTestimonials } from '../api/services';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import './TestimonialsPage.css';

function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    getTestimonials().then(res => {
      if (res.success) {
        // Ensure gallery_images is always an array
        const data = (res.data || []).map(item => ({
          ...item,
          gallery_images: Array.isArray(item.gallery_images) 
            ? item.gallery_images 
            : item.gallery_images ? JSON.parse(item.gallery_images) : []
        }));
        setTestimonials(data);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const renderStars = (rating) => '★'.repeat(rating) + '☆'.repeat(5 - rating);

  const filteredTestimonials = testimonials.filter(t => {
    if (ratingFilter !== 'all' && t.rating !== parseInt(ratingFilter)) return false;
    return true;
  });

  const openModal = (testimonial) => {
    setSelected(testimonial);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelected(null);
    document.body.style.overflow = 'auto';
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="tp-loading-page">Loading testimonials...</div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      {/* Page Banner */}
      <section className="tp-page-banner">
        <div className="tp-banner-overlay">
          <div className="container">
            <h1 className="tp-banner-title">What Our Clients Say</h1>
            <p className="tp-banner-subtitle">
              Real feedback from homeowners, businesses, and government bodies we've worked with across Chhattisgarh.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="tp-stats-bar">
        <div className="container">
          <div className="tp-stats-grid">
            <div className="tp-stat-item">
              <span className="tp-stat-number">{testimonials.length}</span>
              <span className="tp-stat-label">Total Reviews</span>
            </div>
            <div className="tp-stat-item">
              <span className="tp-stat-number">
                {testimonials.filter(t => t.rating === 5).length}
              </span>
              <span className="tp-stat-label">★★★★★ Reviews</span>
            </div>
            <div className="tp-stat-item">
              <span className="tp-stat-number">
                {Math.round(testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length * 10) / 10 || 0}
              </span>
              <span className="tp-stat-label">Average Rating</span>
            </div>
            <div className="tp-stat-item">
              <span className="tp-stat-number">
                {testimonials.filter(t => t.is_active === 1).length}
              </span>
              <span className="tp-stat-label">Active Reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="tp-filters-section">
        <div className="container">
          <div className="tp-filters">
            <button 
              className={`tp-filter-btn ${ratingFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRatingFilter('all')}
            >
              All ({testimonials.length})
            </button>
            {[5, 4, 3, 2, 1].map(r => (
              <button 
                key={r}
                className={`tp-filter-btn ${ratingFilter === String(r) ? 'active' : ''}`}
                onClick={() => setRatingFilter(String(r))}
              >
                {'★'.repeat(r)} ({testimonials.filter(t => t.rating === r).length})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="tp-grid-section">
        <div className="container">
          {filteredTestimonials.length === 0 ? (
            <div className="tp-empty">
              <p>No testimonials found.</p>
            </div>
          ) : (
            <div className="tp-grid">
              {filteredTestimonials.map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className="tp-card" 
                  onClick={() => openModal(testimonial)}
                >
                  <div className="tp-card-header">
                    {testimonial.client_photo ? (
                      <img 
                        src={testimonial.client_photo} 
                        alt={testimonial.client_name} 
                        className="tp-card-avatar" 
                      />
                    ) : (
                      <div className="tp-card-avatar-placeholder">
                        {testimonial.client_name.charAt(0)}
                      </div>
                    )}
                    <div className="tp-card-author">
                      <h4>{testimonial.client_name}</h4>
                      {testimonial.designation && <span>{testimonial.designation}</span>}
                      {testimonial.company && <span className="tp-card-company">{testimonial.company}</span>}
                      <div className="tp-card-stars">{renderStars(testimonial.rating)}</div>
                    </div>
                  </div>
                  <p className="tp-card-message">"{testimonial.message}"</p>
                  {testimonial.gallery_images && testimonial.gallery_images.length > 0 && (
                    <div className="tp-card-gallery">
                      {testimonial.gallery_images.slice(0, 3).map((img, index) => (
                        <img key={index} src={img} alt={`Gallery ${index}`} className="tp-card-gallery-thumb" />
                      ))}
                      {testimonial.gallery_images.length > 3 && (
                        <span className="tp-card-gallery-more">+{testimonial.gallery_images.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="tp-card-click-hint">Click to view full review →</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Full Modal */}
      {selected && (
        <div className="tp-modal-overlay" onClick={closeModal}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tp-modal-close" onClick={closeModal}>✕</button>
            
            <div className="tp-modal-header">
              {selected.client_photo ? (
                <img src={selected.client_photo} alt={selected.client_name} className="tp-modal-avatar" />
              ) : (
                <div className="tp-modal-avatar-placeholder">
                  {selected.client_name.charAt(0)}
                </div>
              )}
              <div>
                <h2>{selected.client_name}</h2>
                {selected.designation && <p>{selected.designation}</p>}
                {selected.company && <p className="tp-modal-company">{selected.company}</p>}
                <div className="tp-modal-stars">{renderStars(selected.rating)}</div>
              </div>
            </div>

            <div className="tp-modal-body">
              <div className="tp-modal-message">
                <h4>Review</h4>
                <p>{selected.message}</p>
              </div>

              {selected.gallery_images && selected.gallery_images.length > 0 && (
                <div className="tp-modal-gallery">
                  <h4>Project Gallery ({selected.gallery_images.length})</h4>
                  <div className="tp-modal-gallery-grid">
                    {selected.gallery_images.map((img, index) => (
                      <img key={index} src={img} alt={`Gallery ${index}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default TestimonialsPage;